import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBloodResults, createBloodResult, updateBloodResult, deleteBloodResult, getAllTreatments } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, X, Sparkles, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import BloodGraphs from '../components/BloodGraphs';
import SoftBlurWrapper from '../components/SoftBlurWrapper';
import { UNITS, isSILocale, convertHaemoglobinForDisplay, convertHaemoglobinForStorage } from '../utils/unitFormatter';
import { formatDisplayDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import useUsageLimit from '../hooks/useUsageLimit';

const BloodResultsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isReadOnly, targetUser, readonlyToken } = useReadOnly();
  const displayUser = isReadOnly ? targetUser : user;
  const userLocale = displayUser?.country || 'AU';
  const [results, setResults] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clinicalRanges, setClinicalRanges] = useState(null);
  
  // Usage limit for soft blur gating
  const { isFreeLimitReached, loading: usageLoading, refetch: refetchUsage } = useUsageLimit();
  
  // Helper to transform results for display (convert haemoglobin for US locale)
  const transformResultsForDisplay = (rawResults) => {
    return rawResults.map(result => ({
      ...result,
      haemoglobin: result.haemoglobin !== null 
        ? convertHaemoglobinForDisplay(result.haemoglobin, userLocale)
        : null
    }));
  };
  
  // Edit state - NO formKey, simple boolean
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    wbc: '',
    lymphocytes: '',
    platelets: '',
    haemoglobin: '',
    neutrophils: '',
    notes: '',
    is_during_illness: false
  });

  useEffect(() => {
    fetchResults();
    fetchClinicalRanges();
    fetchSupplements();
  }, [readonlyToken]); // Re-fetch when entering/exiting readonly mode

  const fetchSupplements = async () => {
    try {
      const data = await getAllTreatments();
      setSupplements(data);
    } catch (error) {
      console.error('Failed to load treatments for timeline');
    }
  };

  const fetchClinicalRanges = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinical-ranges`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setClinicalRanges(data.ranges);
    } catch (error) {
      console.error('Failed to load clinical ranges');
    }
  };

  const fetchResults = async () => {
    try {
      const data = await getBloodResults();
      // Transform haemoglobin values for display based on user locale
      setResults(transformResultsForDisplay(data));
    } catch (error) {
      toast.error('Failed to load blood results');
    } finally {
      setLoading(false);
    }
  };

  // TOP-LEVEL EDIT HANDLER - No effects, no reactive deps
  const handleEdit = async (result) => {
    console.log('🔵 Edit clicked for result:', result.id);
    
    try {
      // Fetch fresh data from backend
      const freshData = await getBloodResults();
      const freshRecord = freshData.find(r => r.id === result.id);
      
      if (!freshRecord) {
        toast.error('Record not found');
        return;
      }
      
      // Set editing state
      setEditingId(freshRecord.id);
      setEditFormData({
        date: freshRecord.date,
        wbc: freshRecord.wbc !== null ? freshRecord.wbc.toString() : '',
        lymphocytes: freshRecord.lymphocytes !== null ? freshRecord.lymphocytes.toString() : '',
        platelets: freshRecord.platelets !== null ? freshRecord.platelets.toString() : '',
        haemoglobin: freshRecord.haemoglobin !== null ? freshRecord.haemoglobin.toString() : '',
        neutrophils: freshRecord.neutrophils !== null ? freshRecord.neutrophils.toString() : '',
        notes: freshRecord.notes || '',
        is_during_illness: freshRecord.is_during_illness || false
      });
      setIsEditing(true);
      
      console.log('✅ Edit state set successfully');
    } catch (error) {
      console.error('Edit failed:', error);
      toast.error('Failed to load record for editing');
    }
  };

  // TOP-LEVEL SAVE HANDLER
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    console.log('💾 Saving edit for:', editingId);
    
    // Convert haemoglobin to storage format (g/L) before saving
    const haemoglobinValue = editFormData.haemoglobin 
      ? convertHaemoglobinForStorage(parseFloat(editFormData.haemoglobin), userLocale)
      : null;
    
    const payload = {
      date: editFormData.date,
      wbc: editFormData.wbc ? parseFloat(editFormData.wbc) : null,
      lymphocytes: editFormData.lymphocytes ? parseFloat(editFormData.lymphocytes) : null,
      platelets: editFormData.platelets ? parseFloat(editFormData.platelets) : null,
      haemoglobin: haemoglobinValue,
      neutrophils: editFormData.neutrophils ? parseFloat(editFormData.neutrophils) : null,
      notes: editFormData.notes || null,
      is_during_illness: editFormData.is_during_illness || false
    };

    try {
      // HTTP PUT request
      await updateBloodResult(editingId, payload);
      console.log('✅ Save successful');
      toast.success('Blood result updated');
      
      // Close modal
      setIsEditing(false);
      
      // Explicit refetch
      console.log('🔄 Refetching results...');
      await fetchResults();
      
      // Reset state explicitly
      setEditingId(null);
      setEditFormData({
        date: '',
        wbc: '',
        lymphocytes: '',
        platelets: '',
        haemoglobin: '',
        neutrophils: '',
        notes: ''
      });
      
      console.log('✅ Edit cycle complete');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to update result');
    }
  };

  // TOP-LEVEL CANCEL HANDLER
  const handleCancelEdit = () => {
    console.log('❌ Edit cancelled');
    setIsEditing(false);
    setEditingId(null);
    setEditFormData({
      date: '',
      wbc: '',
      lymphocytes: '',
      platelets: '',
      haemoglobin: '',
      neutrophils: '',
      notes: ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this result?')) return;
    
    try {
      await deleteBloodResult(id);
      toast.success('Blood result deleted');
      fetchResults();
    } catch (error) {
      toast.error('Failed to delete blood result');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blood-results-page" className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">
            My Progress
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700">See how your blood results change alongside your treatments</p>
        </div>
        <div className="flex">
          <Button
            onClick={() => navigate('/blood-results/add-step-by-step')}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm sm:text-base"
          >
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
            Add Blood Result
          </Button>
        </div>
      </div>

      {/* EDIT MODAL - NO key prop */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-slate-800">Edit Blood Test Result</h2>
              <button
                onClick={handleCancelEdit}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSaveEdit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Test Date</label>
                  <input
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                    required
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">WBC ({UNITS.BLOOD_COUNT})</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editFormData.wbc}
                    onChange={(e) => setEditFormData({ ...editFormData, wbc: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="4.0 - 11.0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Lymphocytes ({UNITS.BLOOD_COUNT})</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editFormData.lymphocytes}
                    onChange={(e) => setEditFormData({ ...editFormData, lymphocytes: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="1.0 - 4.0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Platelets ({UNITS.BLOOD_COUNT})</label>
                  <input
                    type="number"
                    step="1"
                    value={editFormData.platelets}
                    onChange={(e) => setEditFormData({ ...editFormData, platelets: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="150 - 400"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Haemoglobin ({clinicalRanges?.haemoglobin?.unit || UNITS.HEMOGLOBIN_SI})
                  </label>
                  <input
                    type="number"
                    step={clinicalRanges?.haemoglobin?.unit === 'g/dL' ? '0.1' : '1'}
                    value={editFormData.haemoglobin}
                    onChange={(e) => setEditFormData({ ...editFormData, haemoglobin: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder={clinicalRanges ? `${clinicalRanges.haemoglobin.min} - ${clinicalRanges.haemoglobin.max}` : '130 - 180'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Neutrophils ({UNITS.BLOOD_COUNT})</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editFormData.neutrophils}
                    onChange={(e) => setEditFormData({ ...editFormData, neutrophils: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="2.0 - 7.5"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  placeholder="Optional notes..."
                />
              </div>
              
              <div className="flex gap-4">
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
                <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blood Trends Section - Tabs + Single Chart + Timeline */}
      {results.length > 0 && (
        <SoftBlurWrapper isBlurred={isFreeLimitReached && !usageLoading} className="mb-8">
          <BloodGraphs 
            results={results} 
            clinicalRanges={clinicalRanges} 
            supplements={supplements}
          />
        </SoftBlurWrapper>
      )}

      {/* Results List */}
      <h2 className="text-2xl font-bold text-slate-900 mb-6">
        {results.length > 0 ? 'History' : 'No Results Yet'}
      </h2>
      <div className="space-y-4">
        {results.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm text-center">
            <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No results yet</h3>
            <p className="text-slate-600 mb-6">Start tracking by adding your first blood test result</p>
          </div>
        ) : (
          results.map((result) => (
            <div
              key={result.id}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-lg font-semibold text-slate-900">{formatDisplayDate(result.date, userLocale)}</p>
                    {result.last_modified && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full border border-amber-200">
                        Edited
                      </span>
                    )}
                  </div>
                  {result.last_modified && (
                    <p className="text-xs text-slate-500 mb-2">
                      Last modified: {new Date(result.last_modified).toLocaleString()}
                    </p>
                  )}
                  {result.notes && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-800 mb-1">Notes:</p>
                      <p className="text-sm text-blue-900">{result.notes}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {/* EDIT BUTTON - Blood Results ONLY */}
                  <button
                    onClick={() => handleEdit(result)}
                    data-testid={`edit-blood-result-${result.id}`}
                    className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    title="Edit result"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(result.id)}
                    data-testid={`delete-blood-result-${result.id}`}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete result"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {result.wbc !== null && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">WBC</p>
                    <p className="text-lg font-semibold text-slate-900">{result.wbc.toFixed(1)}</p>
                  </div>
                )}
                {result.lymphocytes !== null && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Lymphocytes</p>
                    <p className="text-lg font-semibold text-slate-900">{result.lymphocytes.toFixed(1)}</p>
                  </div>
                )}
                {result.platelets !== null && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Platelets</p>
                    <p className="text-lg font-semibold text-slate-900">{result.platelets}</p>
                  </div>
                )}
                {result.haemoglobin !== null && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Haemoglobin</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {clinicalRanges?.haemoglobin?.unit === 'g/dL' 
                        ? result.haemoglobin.toFixed(1) 
                        : Math.round(result.haemoglobin)}
                    </p>
                  </div>
                )}
                {result.neutrophils !== null && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Neutrophils</p>
                    <p className="text-lg font-semibold text-slate-900">{result.neutrophils.toFixed(1)}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BloodResultsPage;
